import React, { useState } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  CircularProgress,
  Box,
} from "@mui/material";
import { PersonAdd } from "@mui/icons-material";
import axios from "axios";

/**
 * InviteTeamMember Component - Send team invitation emails
 *
 * Allows company admins to invite new team members by sending them
 * an email with the company registration code and signup instructions
 *
 * @param {Object} props - Component props
 * @param {number} props.companyId - The company ID to invite users to
 * @param {string} props.companyName - The company name for display
 * @param {Function} props.onInviteSent - Callback function called after successful invitation
 * @returns {JSX.Element} - The rendered invite team member dialog component
 */
function InviteTeamMember({ companyId, companyName, onInviteSent }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /**
   * Gets the JWT token from cookies for API authentication
   */
  const getAuthToken = () => {
    const cookies = document.cookie.split(";");
    let token = null;

    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === "_auth") {
        token = decodeURIComponent(value);
        break;
      }
    }

    return token;
  };

  /**
   * Handle opening the invite dialog
   */
  const handleClickOpen = () => {
    setEmail("");
    setError("");
    setSuccess("");
    setOpen(true);
  };

  /**
   * Handle closing the dialog and reset form
   */
  const handleClose = () => {
    setOpen(false);
    setEmail("");
    setError("");
    setSuccess("");
    setLoading(false);
  };

  /**
   * Handle email input changes
   */
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    // Clear errors when user starts typing
    if (error) {
      setError("");
    }
  };

  /**
   * Validate email format
   */
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate email
    if (!email.trim()) {
      setError("Email address is required");
      return;
    }

    if (!validateEmail(email.trim())) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const token = getAuthToken();
      if (!token) {
        setError("Authentication required. Please log in again.");
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/invite-team-member`,
        {
          email: email.trim(),
          companyId: companyId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      if (response.data && response.data.success) {
        setSuccess(response.data.message);
        setEmail(""); // Clear the email field

        // Call the callback function if provided
        if (onInviteSent) {
          onInviteSent();
        }

        // Auto-close dialog after 2 seconds
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setError("Failed to send invitation. Please try again.");
      }
    } catch (error) {
      console.error("Error sending invitation:", error);

      if (error.response && error.response.data && error.response.data.error) {
        setError(error.response.data.error);
      } else if (error.response && error.response.status === 401) {
        setError("Your session has expired. Please log in again.");
      } else if (error.response && error.response.status === 403) {
        setError("You do not have permission to invite team members.");
      } else {
        setError("Failed to send invitation. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant='contained'
        startIcon={<PersonAdd />}
        onClick={handleClickOpen}
        sx={{ minWidth: "auto" }}
      >
        Invite to Team
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          component: "form",
          onSubmit: handleSubmit,
        }}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Send an invitation email to add a new member to{" "}
            <strong>{companyName}</strong>. They'll receive an email with your
            company's registration code and signup instructions.
          </DialogContentText>

          {error && (
            <Alert
              severity='error'
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity='success'
              sx={{ mb: 2 }}
            >
              {success}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              required
              fullWidth
              label='Email Address'
              type='email'
              value={email}
              onChange={handleEmailChange}
              disabled={loading}
              variant='outlined'
              placeholder='Enter the email address of the person you want to invite'
              helperText="They'll receive an email with your company's registration code"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            variant='outlined'
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type='submit'
            variant='contained'
            disabled={loading || !email.trim()}
            startIcon={loading ? <CircularProgress size={16} /> : <PersonAdd />}
          >
            {loading ? "Sending Invitation..." : "Send Invitation"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default InviteTeamMember;
